import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from 'recharts';
import { BellIcon, AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react';
const donutData = [{
  name: 'Google',
  value: 400
}, {
  name: 'Meta',
  value: 300
}, {
  name: 'TikTok',
  value: 300
}, {
  name: 'LinkedIn',
  value: 200
}];
const COLORS = ['#00D4AA', '#6366F1', '#F59E0B', '#EF4444'];
const sparklineData = [{
  val: 10
}, {
  val: 15
}, {
  val: 12
}, {
  val: 25
}, {
  val: 18
}, {
  val: 45
}, {
  val: 20
}, {
  val: 22
}];
const stackedData = [{
  name: 'W1',
  clicks: 4000,
  impressions: 2400
}, {
  name: 'W2',
  clicks: 3000,
  impressions: 1398
}, {
  name: 'W3',
  clicks: 2000,
  impressions: 9800
}, {
  name: 'W4',
  clicks: 2780,
  impressions: 3908
}];
const horizontalData = [{
  name: 'Direct',
  value: 80
}, {
  name: 'Organic',
  value: 60
}, {
  name: 'Paid',
  value: 95
}];
const forecastData = [{
  day: '1',
  actual: 100,
  forecast: 100
}, {
  day: '2',
  actual: 120,
  forecast: 115
}, {
  day: '3',
  actual: 110,
  forecast: 125
}, {
  day: '4',
  actual: null,
  forecast: 140
}, {
  day: '5',
  actual: null,
  forecast: 160
}];
export function FeaturesSection() {
  return <section id="insights" className="py-24 px-6 bg-navy-900" data-id="element-19">
      <div className="max-w-7xl mx-auto" data-id="element-20">
        <div className="mb-16" data-id="element-21">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-22">
            The Roguehaven Command Center
          </h2>
          <p className="text-gray-400 max-w-2xl" data-id="element-23">
            Modular, real-time widgets designed to give you absolute clarity
            over your ad performance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-24">
          {/* Widget 1: Nexus Sync */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-25">
            <div className="mb-4" data-id="element-26">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-27">
                <div className="w-2 h-2 rounded-full bg-accent-teal" data-id="element-28"></div>
                Nexus Sync
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-29">
                Unified data ingestion
              </p>
            </div>
            <div className="flex-grow h-40" data-id="element-30">
              <ResponsiveContainer width="100%" height="100%" data-id="element-31">
                <PieChart data-id="element-32">
                  <Pie data={donutData} innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value" stroke="none" data-id="element-33">
                    {donutData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-34" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  backgroundColor: '#131825',
                  borderColor: '#1E2536',
                  color: '#fff'
                }} itemStyle={{
                  color: '#fff'
                }} data-id="element-35" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Widget 2: Pulse Monitor */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-36">
            <div className="mb-4" data-id="element-37">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-38">
                <div className="w-2 h-2 rounded-full bg-accent-red" data-id="element-39"></div>
                Pulse Monitor
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-40">
                Real-time anomaly detection
              </p>
            </div>
            <div className="flex-grow h-40" data-id="element-41">
              <ResponsiveContainer width="100%" height="100%" data-id="element-42">
                <AreaChart data={sparklineData} data-id="element-43">
                  <defs data-id="element-44">
                    <linearGradient id="colorPulse" x1="0" y1="0" x2="0" y2="1" data-id="element-45">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} data-id="element-46" />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} data-id="element-47" />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="val" stroke="#EF4444" strokeWidth={2} fill="url(#colorPulse)" data-id="element-48" />
                  {/* Highlight anomaly */}
                  <circle cx="65%" cy="20%" r="4" fill="#EF4444" className="animate-ping" data-id="element-49" />
                  <circle cx="65%" cy="20%" r="4" fill="#EF4444" data-id="element-50" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Widget 3: Verdikt Reports */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-51">
            <div className="mb-4" data-id="element-52">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-53">
                <div className="w-2 h-2 rounded-full bg-accent-indigo" data-id="element-54"></div>
                Verdikt Reports
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-55">
                Automated client reporting
              </p>
            </div>
            <div className="flex-grow h-40" data-id="element-56">
              <ResponsiveContainer width="100%" height="100%" data-id="element-57">
                <BarChart data={stackedData} margin={{
                top: 10,
                right: 0,
                left: -25,
                bottom: 0
              }} data-id="element-58">
                  <XAxis dataKey="name" stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} data-id="element-59" />
                  <YAxis stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} data-id="element-60" />
                  <Bar dataKey="impressions" stackId="a" fill="#1E2536" radius={[0, 0, 4, 4]} data-id="element-61" />
                  <Bar dataKey="clicks" stackId="a" fill="#6366F1" radius={[4, 4, 0, 0]} data-id="element-62" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Widget 4: Cipher Attribution */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-63">
            <div className="mb-4" data-id="element-64">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-65">
                <div className="w-2 h-2 rounded-full bg-accent-amber" data-id="element-66"></div>
                Cipher Attribution
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-67">
                Cross-channel modeling
              </p>
            </div>
            <div className="flex-grow h-40" data-id="element-68">
              <ResponsiveContainer width="100%" height="100%" data-id="element-69">
                <BarChart data={horizontalData} layout="vertical" margin={{
                top: 0,
                right: 0,
                left: -20,
                bottom: 0
              }} data-id="element-70">
                  <XAxis type="number" hide data-id="element-71" />
                  <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={10} tickLine={false} axisLine={false} data-id="element-72" />
                  <Bar dataKey="value" fill="#F59E0B" radius={[0, 4, 4, 0]} barSize={12} data-id="element-73" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Widget 5: Sentinel Alerts */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-74">
            <div className="mb-4" data-id="element-75">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-76">
                <BellIcon className="w-3 h-3 text-white" data-id="element-77" />
                Sentinel Alerts
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-78">
                Smart budget notifications
              </p>
            </div>
            <div className="flex-grow flex flex-col justify-center gap-3" data-id="element-79">
              <div className="bg-navy-900 border border-accent-red/20 rounded p-3 flex items-start gap-3" data-id="element-80">
                <AlertTriangleIcon className="w-4 h-4 text-accent-red shrink-0 mt-0.5" data-id="element-81" />
                <div data-id="element-82">
                  <p className="text-xs text-white font-medium" data-id="element-83">
                    CPA Spike Detected
                  </p>
                  <p className="text-[10px] text-gray-500 font-mono mt-1" data-id="element-84">
                    Meta Ads • Campaign Alpha
                  </p>
                </div>
              </div>
              <div className="bg-navy-900 border border-accent-teal/20 rounded p-3 flex items-start gap-3" data-id="element-85">
                <CheckCircle2Icon className="w-4 h-4 text-accent-teal shrink-0 mt-0.5" data-id="element-86" />
                <div data-id="element-87">
                  <p className="text-xs text-white font-medium" data-id="element-88">
                    Budget Pacing Optimal
                  </p>
                  <p className="text-[10px] text-gray-500 font-mono mt-1" data-id="element-89">
                    Google Ads • Q3 Search
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Widget 6: Lumen Forecasting */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="bg-navy-800 border border-navy-700 rounded-xl p-6 flex flex-col" data-id="element-90">
            <div className="mb-4" data-id="element-91">
              <h3 className="text-white font-medium flex items-center gap-2" data-id="element-92">
                <div className="w-2 h-2 rounded-full bg-white" data-id="element-93"></div>
                Lumen Forecasting
              </h3>
              <p className="text-xs text-gray-400 mt-1" data-id="element-94">
                Predictive spend & ROAS
              </p>
            </div>
            <div className="flex-grow h-40" data-id="element-95">
              <ResponsiveContainer width="100%" height="100%" data-id="element-96">
                <LineChart data={forecastData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-97">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2536" vertical={false} data-id="element-98" />
                  <XAxis dataKey="day" stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} data-id="element-99" />
                  <YAxis stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} data-id="element-100" />
                  <Line type="monotone" dataKey="actual" stroke="#00D4AA" strokeWidth={2} dot={{
                  r: 3,
                  fill: '#00D4AA'
                }} data-id="element-101" />
                  <Line type="monotone" dataKey="forecast" stroke="#6366F1" strokeWidth={2} strokeDasharray="5 5" dot={false} data-id="element-102" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}